import React, { useContext, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { useCreateTariff } from '../../../api/bitedb/tariffs/tariffs.hooks';
import {
  CustomLabel,
  CustomSwitch,
} from 'components/Common/MUIStyledComponents';

interface IAddSingleTariffModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  database: any;
  handleRefresh?: any;
  // Kept in interface for future compatibility, but not used currently
  onDuplicatesDetected?: (payload: any) => void;
}

export const AddSingleTariffModal: React.FC<IAddSingleTariffModalProps> = ({
  open,
  handleClose,
  title = 'Add Tariff',
  database,
  handleRefresh,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const createTariffMutation = useCreateTariff();

  // --- Tariff State ---
  const [htsCode, setHtsCode] = useState<string>('');
  const [originCountry, setOriginCountry] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // Defaults matching bulk upload logic
  const [productValue, setProductValue] = useState<number>(1000);
  const [transMethod, setTransMethod] = useState<string>('ocean');

  // Default dates to today YYYY-MM-DD
  const todayStr = new Date().toISOString().split('T')[0];
  const [entryDate, setEntryDate] = useState<string>(todayStr);
  const [loadingDate, setLoadingDate] = useState<string>(entryDate);
  const [dateStrategy, setDateStrategy] = useState<'current' | 'original'>('original');

  const [materialPct, setMaterialPct] = useState<number>(0.4);
  const [materialWeight, setMaterialWeight] = useState<number>(100);
  // Reported quantity + the unit it was reported in. Blank by default and
  // sent as null when blank: these price the specific (per-unit) portion of a
  // duty rate, so a made-up default would invent duty on every line carrying a
  // rate like "2.3% + $1.05/kg". Left empty, BITE prices the ad valorem half
  // and flags the line "Specific Rate Missing Unit Quantity".
  const [quantity, setQuantity] = useState<string>('');
  const [quantityUom, setQuantityUom] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [countryOfSmeltPour, setCountryOfSmeltPour] = useState<string>('');
  const [useFTA, setUseFTA] = useState<boolean>(false);

  const validateInputs = (): boolean => {
    // 1. HTS Code Validation: Must resolve to exactly 10 digits
    const cleanHts = htsCode.replace(/\D/g, '');
    if (cleanHts.length !== 10) {
      enqueueSnackbar('HTS Code must contain exactly 10 digits.', {
        variant: 'error',
      });
      return false;
    }

    // 2. Country Validation: Must be exactly 2 characters (ISO 2)
    const cleanCountry = originCountry.trim();
    if (
      !cleanCountry ||
      cleanCountry.length !== 2 ||
      !/^[a-zA-Z]{2}$/.test(cleanCountry)
    ) {
      enqueueSnackbar(
        'Origin Country must be a valid 2-letter ISO code (e.g. CN, US).',
        { variant: 'error' },
      );
      return false;
    }

    return true;
  };

  const handleTariffAddition = async () => {
    if (createTariffMutation.isPending) {
      return;
    }

    if (!validateInputs()) {
      return;
    }

    const tariffData = {
      // Format as 0000.00.00.00
      hts_code: htsCode
        .replace(/\D/g, '')
        .replace(/^(\d{4})(\d{2})(\d{2})(\d{2})$/, '$1.$2.$3.$4'),
      country_of_origin: originCountry.toUpperCase(),
      description: description,
      product_value: productValue,
      trans_method: transMethod,
      entry_date: new Date(entryDate).toISOString(),
      loading_date: new Date(loadingDate).toISOString(),
      date_strategy: dateStrategy,
      material_pct: materialPct,
      material_weight: String(materialWeight),
      quantity: quantity.trim() === '' ? null : Number(quantity),
      quantity_uom: quantityUom.trim() === '' ? null : quantityUom.trim(),
      product_code: String(productCode),
      use_fta: Boolean(useFTA),
      country_of_smelt_pour: countryOfSmeltPour || originCountry.toUpperCase(),
      database_id: database.id,
      created_by: cur_user.basics?.id,
    };

    createTariffMutation.mutate(
      {
        tariffData: tariffData,
        teamId: cur_user.selected_team?.id,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Tariff added successfully', { variant: 'success' });

          // Reset form
          setHtsCode('');
          setOriginCountry('');
          setDescription('');
          setProductValue(1000);
          setTransMethod('ocean');
          setEntryDate(todayStr);
          setLoadingDate(todayStr);
          setDateStrategy('original');
          setMaterialPct(0.4);
          setMaterialWeight(100);
          setCountryOfSmeltPour('');

          handleClose();
          if (handleRefresh) {
            handleRefresh();
          }
        },
        onError: (error: any) => {
          console.error('Error adding tariff:', error);
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Error adding tariff';
          enqueueSnackbar(errorMessage, { variant: 'error' });
        },
      },
    );
  };

  return (
    <>
      <Dialog
        className='dialog_box'
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
            maxWidth: '800px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          id='tariff-modal-title'
          className='modal-header-content modal-title'
        >
          {title}
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <div className='audit-add-modal-cont-body-frame'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='audit-add-modal-head-cont-supporting'>
                  <h1>Enter Tariff information manually</h1>
                  <p>Tariff Item</p>
                </div>
                <div className='audit-add-modal-cont-body-frame-form-row'>
                  <div className='grid-layout' style={{ width: '100%' }}>
                    {/* HTS CODE */}
                    <div>
                      <label
                        htmlFor='hts-code'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        HTS Code (10 digits){' '}
                        <span className='required-star'>*</span>
                      </label>
                      <input
                        type='text'
                        id='hts-code'
                        value={htsCode}
                        onChange={(e) => setHtsCode(e.target.value)}
                        placeholder='e.g. 6211.11.10.10'
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* ORIGIN COUNTRY */}
                    <div>
                      <label
                        htmlFor='origin-country'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Origin Country (ISO 2){' '}
                        <span className='required-star'>*</span>
                      </label>
                      <input
                        type='text'
                        id='origin-country'
                        value={originCountry}
                        onChange={(e) =>
                          setOriginCountry(e.target.value.toUpperCase())
                        }
                        placeholder='e.g. CN, US, MX'
                        maxLength={2}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* DESCRIPTION */}
                    <div className='col-span-2'>
                      <label
                        htmlFor='tariff-description'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Description
                      </label>
                      <textarea
                        id='tariff-description'
                        rows={2}
                        placeholder='Product description (optional)'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      ></textarea>
                    </div>

                    {/* PRODUCT VALUE */}
                    <div>
                      <label
                        htmlFor='product-value'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Product Value ($)
                      </label>
                      <input
                        type='number'
                        id='product-value'
                        value={productValue}
                        onChange={(e) =>
                          setProductValue(Number(e.target.value))
                        }
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* TRANSPORT METHOD */}
                    <div>
                      <label
                        htmlFor='trans-method'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Transport Method
                      </label>
                      <select
                        id='trans-method'
                        value={transMethod}
                        onChange={(e) => setTransMethod(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      >
                        <option value='ocean'>Ocean</option>
                        <option value='air'>Air</option>
                        <option value='land'>Land</option>
                      </select>
                    </div>

                    {/* ENTRY DATE */}
                    <div>
                      <label
                        htmlFor='entry-date'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Entry Date
                      </label>
                      <input
                        type='date'
                        id='entry-date'
                        value={entryDate}
                        onChange={(e) => setEntryDate(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* LOADING DATE */}
                    <div>
                      <label
                        htmlFor='loading-date'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Loading Date
                      </label>
                      <input
                        type='date'
                        id='loading-date'
                        value={loadingDate}
                        onChange={(e) => setLoadingDate(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div className='col-span-2'>
                      <label
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        style={{ marginBottom: '10px' }}
                      >
                        Tariff date strategy
                      </label>
                      <div className='audit-add-modal-cont-body-frame'>
                        <div className='bill-container-fit-screen-content-row-selection'>
                          <div
                            className='bill-container-fit-screen-content-row-checkbox'
                            onClick={() => setDateStrategy('current')}
                          >
                            <div className='bill-container-fit-screen-content-row-input'>
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon ${
                                  dateStrategy === 'current' ? 'selected' : ''
                                }`}
                              >
                                <div
                                  className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                    dateStrategy === 'current' ? 'selected' : ''
                                  }`}
                                />
                              </div>
                            </div>
                            <div className='bill-container-fit-screen-content-row-checkbox-text'>
                              <p>Re-calculate the transaction using the current date each time</p>
                            </div>
                          </div>
                          <div
                            className='bill-container-fit-screen-content-row-checkbox'
                            onClick={() => setDateStrategy('original')}
                          >
                            <div className='bill-container-fit-screen-content-row-input'>
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon ${
                                  dateStrategy === 'original' ? 'selected' : ''
                                }`}
                              >
                                <div
                                  className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                    dateStrategy === 'original' ? 'selected' : ''
                                  }`}
                                />
                              </div>
                            </div>
                            <div className='bill-container-fit-screen-content-row-checkbox-text'>
                              <p>Re-calculate using the original loading and entry dates</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MATERIAL PCT */}
                    <div>
                      <label
                        htmlFor='material-pct'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Material % (0.0 - 1.0)
                      </label>
                      <input
                        type='number'
                        id='material-pct'
                        step='0.01'
                        value={materialPct}
                        onChange={(e) => setMaterialPct(Number(e.target.value))}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* MATERIAL WEIGHT */}
                    <div>
                      <label
                        htmlFor='material-weight'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Ad Valorem
                      </label>
                      <input
                        type='number'
                        id='material-weight'
                        value={materialWeight}
                        onChange={(e) =>
                          setMaterialWeight(Number(e.target.value))
                        }
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* REPORTED QUANTITY */}
                    <div>
                      <label
                        htmlFor='tariff-quantity'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Quantity
                      </label>
                      <input
                        type='number'
                        id='tariff-quantity'
                        step='any'
                        min='0'
                        placeholder='e.g. 77.76'
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* REPORTED QUANTITY UOM */}
                    <div>
                      <label
                        htmlFor='tariff-quantity-uom'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Unit of Measure
                      </label>
                      <input
                        type='text'
                        id='tariff-quantity-uom'
                        placeholder='e.g. KG, NO, DOZ'
                        value={quantityUom}
                        onChange={(e) => setQuantityUom(e.target.value)}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* SMELT POUR COUNTRY */}
                    <div>
                      <label
                        htmlFor='smelt-pour'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Country of Smelt/Pour (ISO 2)
                      </label>
                      <input
                        type='text'
                        id='smelt-pour'
                        value={countryOfSmeltPour}
                        onChange={(e) => setCountryOfSmeltPour(e.target.value)}
                        placeholder='Optional'
                        maxLength={2}
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* FTA */}
                    <div>
                      <label
                        htmlFor='smelt-pour'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Use FTA (If Available)
                      </label>
                      <CustomLabel
                        control={
                          <CustomSwitch
                            checked={useFTA}
                            onChange={(e: any) => {
                              if (e.target.value === 'on') {
                                setUseFTA(true);
                              } else {
                                setUseFTA(false);
                              }
                            }}
                            name='fta'
                          />
                        }
                        label={useFTA ? 'Yes' : 'No'}
                      />
                    </div>

                    {/* Product Code */}
                    <div>
                      <label
                        htmlFor='product-code'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Product Code
                      </label>
                      <input
                        type='text'
                        id='product-code'
                        value={productCode}
                        onChange={(e) =>
                          setProductCode(e.target.value.toUpperCase())
                        }
                        placeholder='Optional'
                        className='audit-add-modal-cont-body-frame-form-input'
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='audit-modal-cont-footer'>
                <div className='audit-modal-cont-footer-content'>
                  <div
                    className='audit-modal-cont-footer-content-btn-cancel'
                    onClick={handleClose}
                  >
                    <div className='audit-modal-cont-footer-content-btn-text'>
                      Cancel
                    </div>
                  </div>
                  <div
                    className='audit-modal-cont-footer-content-btn-upload'
                    onClick={
                      !createTariffMutation.isPending
                        ? handleTariffAddition
                        : undefined
                    }
                  >
                    <div
                      className={`audit-modal-cont-footer-content-btn-text ${
                        createTariffMutation.isPending
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      style={{ color: '#FFF' }}
                    >
                      {createTariffMutation.isPending ? (
                        <span className='flex items-center justify-center'>
                          <CircularProgress
                            style={{
                              color: '#FFF',
                              width: '25px',
                              height: '25px',
                              marginRight: '10px',
                            }}
                          />
                          Saving...
                        </span>
                      ) : (
                        'Save'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
